<template>
  <div class="content-dashboard">
    <h3>Commandes clients</h3>

    <div class="row mt-5">
      <div class="col-lg-12">
        <div class="d-flex gap-3">
          <!-- Date After Filter -->
          <div class="date-picker-wrapper">
            <a href="#" class="btn btn-outline-orange medium-btn" @click.prevent="openDatePicker('after')">
              <i class="ti ti-calendar orange-color pe-2"></i>
              {{ filters.createdAtAfter ? formatDate(filters.createdAtAfter) : "Date de début" }}
            </a>
            <input type="date" v-model="filters.createdAtAfter" class="date-input" ref="dateAfterPicker"
              @change="fetchOrders" />
          </div>

          <!-- Date Before Filter -->
          <div class="date-picker-wrapper">
            <a href="#" class="btn btn-outline-orange medium-btn" @click.prevent="openDatePicker('before')">
              <i class="ti ti-calendar orange-color pe-2"></i>
              {{ filters.createdAtBefore ? formatDate(filters.createdAtBefore) : "Date de fin" }}
            </a>
            <input type="date" v-model="filters.createdAtBefore" class="date-input" ref="dateBeforePicker"
              @change="fetchOrders" />
          </div>

          <!-- Status Filter -->
          <select id="statut" v-model="filters.status" class="form-select w-25" @change="fetchOrders">
            <option value="">Tous les statuts</option>
            <option v-for="status in orderStatuses" :key="status.id" :value="status.id">
              {{ status.title }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="row mb-5 mt-4">
      <div class="col-lg-12">
        <template v-if="isLoadingOrders">
          <div class="loading">Chargement des commandes...</div>
        </template>
        <template v-else-if="orders.length > 0">
          <div v-for="order in orders" :key="order.tokenValue" class="gray-box d-block mb-3 commandes-client-block">
            <div class="accordion-block-box justify-content-between align-items-center d-flex gap-3">
              <div class="accordion-block-info w-70">
                <div class="accordion-block-info-top d-flex align-items-center gap-3">
                  <!-- <span class="border-title">{{ order.state }}</span> -->
                  <p v-if="order.customerName">
                    <span class="ps-1">{{ order.customerName }}</span>
                  </p>
                  <p>
                    <i class="ti ti-calendar pe-2 blue-color"></i>
                    <small>{{ formatDate(order.checkout_completed_at?.date) }}</small>
                  </p>
                  <span :class="['status-title', getShippingStateClass(order.shipped)]">
                    {{ translateShippingState(order.shipped) }}
                  </span>
                </div>

                <div class="accordion-block-info-bottom mt-3 mb-2">
                  <ul class="d-flex align-items-center gap-2">
                    <li>
                      <span class="font-bold font-16">{{ order.number }}</span>
                    </li>
                    <li>
                      <span class="font-bold font-16">{{ order.nb_articles }} article{{ order.nb_articles > 1 ? "s" : ""
                        }}</span>
                    </li>
                    <li>
                      <span class="font-bold font-16">{{ order.itemsTotal ? (order.itemsTotal / 100).toFixed(2) : "N/A"
                        }}€</span>
                    </li>
                  </ul>
                </div>

                <p class="font-15 position-relative ps-4 text-muted">
                  <i class="ti-location-pin blue-color pe-e font-20 left_3 position-absolute"></i>
                  {{ formatAddress(order.shippingAddress) }}
                </p>
              </div>
              <div class="actions-btns d-flex flex-column gap-2">
                <a href="#" class="btn btn-orange" data-bs-toggle="offcanvas" data-bs-target="#sidePanel"
                  @click.prevent="showOrderDetails(order.tokenValue)">Voir les détails</a>
                <a :href="order.urlSuivi" target="_blank" class="btn btn-outline-orange" v-if="order.shipped">Suivre mon
                  colis</a>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="alert alert-info">Aucune commande trouvée.</div>
        </template>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination mt-4 d-flex justify-content-center align-items-center">
      <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="pagination-btn prev">
        <i class="ti ti-angle-left"></i>
      </button>
      <div class="pagination-content">
        <div class="pagination-btns mt-4">
          <button v-for="page in totalPages" :key="page" @click="changePage(page)"
            :class="['pagination-btn-numbers', 'mx-1', { active: currentPage === page }]">
            {{ page }}
          </button>
        </div>
        <span class="pagination-info">Sur {{ totalPages }}</span>
      </div>

      <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="pagination-btn next">
        <i class="ti ti-angle-right"></i>
      </button>
    </div>

    <!-- Offcanvas for order details -->
    <div class="offcanvas offcanvas-end w-35" tabindex="-1" id="sidePanel" aria-labelledby="sidePanelLabel">
      <div class="offcanvas-header justify-content-end">
        <button type="button" class="close-link" data-bs-dismiss="offcanvas" aria-label="Fermer">
          <i class="ti ti-close"></i>
        </button>
      </div>
      <template v-if="loadingOrderDetails">
        <div class="loading">Chargement des détails de la commande...</div>
      </template>
      <div class="offcanvas-body" v-else-if="selectedOrder">
        <div class="commandes-panel">
          <h3 class="mb-5 mx-4">Détails commande</h3>

          <Carousel v-if="getOrderImages.length > 0" :items-to-show="1" :wrap-around="true" :autoplay="false"
            :pause-autoplay-on-hover="true" :pagination-enabled="true" :navigation-enabled="false">
            <Slide v-for="(img, index) in getOrderImages" :key="index">
              <img :src="img" class="w-60" alt="Product image" />
            </Slide>
          </Carousel>
          <div v-else class="alert alert-info">Aucune image disponible.</div>

          <div class="commande-inner m-4">
            <div class="d-flex gap-3 align-items-center my-4">
              <!-- <span class="border-title">{{ selectedOrder.state || "N/A" }}</span> -->
              <p class="mb-0">
                <i class="ti ti-calendar pe-2 blue-color"></i>
                <small>{{ formatDate(selectedOrder.checkout_completed_at?.date) }}</small>
              </p>

              <span :class="['status-title', getShippingStateClass(selectedOrder.shipped)]">
                {{ translateShippingState(selectedOrder.shipped) }}
              </span>
            </div>

            <div class="price-commande">
              <table class="table">
                <thead>
                  <th>{{ selectedOrder.number || "N/A" }}</th>
                  <th></th>
                  <th>
                    {{ (selectedOrder.items || []).length }} article{{
                    (selectedOrder.items || []).length > 1 ? "s" : ""
                    }}
                  </th>
                </thead>
                <tbody>
                  <tr v-for="item in selectedOrder.items || []" :key="item.id">
                    <td class="px-0">
                      <p class="mb-0">{{ item.productName || "N/A" }} ( {{ item.quantity }} )</p>
                      <!-- <span class="dark-color-1 text-uppercase font-15">REF: {{ item.variantDetails?.ref || "N/A"
                        }}</span> -->
                    </td>
                    <td class="px-0 w-30">
                      <small class="poids">{{ item.variantDetails?.poidsNet || 0 }}</small>
                    </td>
                    <td class="px-0" >
                      <p class="mb-0" v-if="item.subtotal===0">Offert</p>
                      <p class="mb-0" v-else>{{ (item.subtotal / 100).toFixed(2) }}€</p>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="d-flex justify-content-between align-items-center mt-2">
                <div class="final-price-note">
                  <strong>Total TVA inclus</strong>
                </div>
                <div class="final-price">
                  <strong>{{ (selectedOrder.total / 100).toFixed(2) }}€</strong>
                </div>

              </div>

              <div class="adress-commande mt-2">
                <p class="mb-0">
                  <i class="ti ti-location-pin blue-color"></i>
                  {{ formatAddress(selectedOrder.shippingAddress) }}
                </p>
              </div>

              <div class="mt-4 d-flex gap-3">
                <a href="#" class="btn btn-orange">Recommander</a>
                <!-- Factures buttons -->
                <template v-if="selectedOrder.factures && selectedOrder.factures.length > 0">
                  <a
                    v-for="facture in selectedOrder.factures"
                    :key="facture.facture_pino"
                    href="#"
                    class="btn btn-outline-orange"
                    :class="{ disabled: downloadingFactures[facture.facture_pino] }"
                    @click.prevent="downloadFacture(facture.facture_pino, facture.facture_prefpino)"
                  >
                    <i
                      v-if="downloadingFactures[facture.facture_pino]"
                      class="ti ti-loader pe-2 spinning me-2"
                    ></i>
                    <i v-else class="ti ti-download pe-2"></i>
                    <span>Télécharger la facture</span>
                  </a>
                </template>
                <a v-else href="#" class="btn btn-outline-orange disabled">
                  <i class="ti ti-download pe-2"></i>
                  <span>Télécharger la facture</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="offcanvas-body">
        <div class="loading">Chargement des détails de la commande...</div>
      </div>
    </div>
  </div>
</template>

<script>
  import { getClientsOrders, getClientsOrderDetails, getOrderStatuses } from "@/api/orders";
  import { useFactureStore } from "@/stores/factureStore";
  import { useToast } from "vue-toastification";
  import { Carousel, Slide } from "vue3-carousel";
  import "vue3-carousel/carousel.css";

  export default {
    name: "CommandesClientView",
    components: {
      Carousel,
      Slide,
    },
    data() {
      return {
        orders: [],
        orderStatuses: [],
        selectedOrder: null,
        isLoadingOrders: false,
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        filters: {
          status: "",
          createdAtAfter: "",
          createdAtBefore: "",
        },
        loadingOrderDetails: false,
        downloadingFactures: {}, // Track downloading state for each facture
      };
    },
    computed: {
      getOrderImages() {
        if (!this.selectedOrder?.items) return [];
        const images = this.selectedOrder.items.flatMap(
          item => item.variantDetails?.imagesDetails?.map(img => img.path) || []
        );
        return [...new Set(images)];
      },
    },
    setup() {
      const factureStore = useFactureStore();
      const toast = useToast();
      return { factureStore, toast };
    },
    methods: {
      async fetchOrderStatuses() {
        this.isLoadingStatuses = true;
        try {
          const response = await getOrderStatuses();
          this.orderStatuses = response.data || [];
        } catch (error) {
          console.error("Failed to fetch order statuses:", error);
          this.orderStatuses = [];
        } finally {
          this.isLoadingStatuses = false;
        }
      },
      async fetchOrders() {
        this.isLoadingOrders = true;
        try {
          const response = await getClientsOrders({
            page: this.currentPage,
            itemsPerPage: this.itemsPerPage,
            status: this.filters.status || null,
            createdAtAfter: this.filters.createdAtAfter || null,
            createdAtBefore: this.filters.createdAtBefore || null,
          });
          this.orders = response.orders;
          this.totalItems = response.totalItems;
          this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        } catch (error) {
          console.error("Failed to fetch orders:", error);
          this.orders = [];
          this.totalItems = 0;
          this.totalPages = 1;
        } finally {
          this.isLoadingOrders = false;
        }
      },
      async showOrderDetails(tokenValue) {
        this.loadingOrderDetails = true;
        this.selectedOrder = null; 
        const getfactures=true;
        try {
          const response = await getClientsOrderDetails(tokenValue,getfactures);
          this.selectedOrder = response.data || null;
        } catch (error) {
          console.error("Erreur lors du chargement des détails de la commande:", error);
          this.selectedOrder = null;
        } finally {
          this.loadingOrderDetails = false;
        }
      },
      async downloadFacture(pino, prefPino) {
        // Set downloading state for this specific facture
        this.downloadingFactures[pino] = true;
        
        try {
          await this.factureStore.downloadFacture(pino, prefPino);
        } catch (error) {
          console.error("Erreur lors du téléchargement de la facture:", error);
          this.toast.error("Erreur lors du téléchargement de la facture");
        } finally {
          // Reset downloading state
          this.downloadingFactures[pino] = false;
        }
      },
      formatAddress(address) {
        if (!address) return "Adresse non disponible";
        const { street, city, postcode, provinceName, countryCode } = address;
        const parts = [street, city, postcode, provinceName, countryCode === "FR" ? "France" : countryCode].filter(
          part => part && part.trim() !== ""
        );
        return parts.length > 0 ? parts.join(", ") : "Adresse non disponible";
      },
      formatDate(date) {
        if (!date) return "--";
        try {
          const d = new Date(date);
          if (isNaN(d)) return "--";
          return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        } catch (error) {
          console.error("Error formatting date:", error);
          return "--";
        }
      },
      openDatePicker(type) {
        if (type === "after") {
          this.$refs.dateAfterPicker.showPicker();
        } else if (type === "before") {
          this.$refs.dateBeforePicker.showPicker();
        }
      },
      changePage(page) {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
          this.currentPage = page;
          this.fetchOrders();
        }
      },

        translateShippingState(state) {
        const stateMap = {
         false: "en cours de préparation",
          true: "Expédié",
        };
        return stateMap[state] || "N/A";
      },
      getShippingStateClass(state) {
        const classMap = {
          false: "blue-state",
          true: "green-state",
        };
        return classMap[state] || "bg-light text-dark";
      },
    },
    watch: {
      "filters.status": function () {
        this.currentPage = 1;
        this.fetchOrders();
      },
      "filters.createdAtAfter": function () {
        this.currentPage = 1;
        this.fetchOrders();
      },
      "filters.createdAtBefore": function () {
        this.currentPage = 1;
        this.fetchOrders();
      },
    },
    mounted() {
      this.fetchOrderStatuses();
      this.fetchOrders();
    },
  };
</script>

<style scoped>
  .date-picker-wrapper {
    position: relative;
    display: inline-block;
  }

  .date-input {
    position: absolute;
    top: 100%;
    left: 0;
    opacity: 0;
    width: 0;
    height: 0;
    z-index: -1;
  }

  .date-input:focus,
  .date-input:active {
    opacity: 1;
    width: auto;
    height: auto;
    z-index: 1000;
  }

  .commandes-panel table thead {
    border-bottom: 1px solid #dee2e6;
  }
  .left_3 {
    left: -3px;
  }

  .spinning {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Responsive Styles */
  @media (max-width: 991px) {
    .w-35 {
      width: 50% !important;
    }
    
    .accordion-block-info.w-70 {
      width: 60% !important;
    }
  }

  @media (max-width: 768px) {
    .content-dashboard h3 {
      font-size: 20px;
      margin-bottom: 15px;
    }

    /* Filters */
    .row.mt-5 {
      margin-top: 1rem !important;
    }

    .col-lg-12 .d-flex.gap-3 {
      flex-direction: column;
      gap: 10px !important;
    }

    .date-picker-wrapper {
      width: 100%;
      display: block;
    }

    .date-picker-wrapper .btn {
      width: 100%;
      justify-content: center;
    }

    .form-select.w-25 {
      width: 100% !important;
      height: 35px;
    }

    /* Order List */
    .accordion-block-box {
      flex-direction: column;
      align-items: flex-start !important;
    }

    .accordion-block-info.w-70 {
      width: 100% !important;
    }

    .accordion-block-info-top {
      flex-wrap: wrap;
      gap: 8px !important;
    }

    .accordion-block-info-bottom ul {
      flex-wrap: wrap;
      gap: 10px !important;
    }
    
    .actions-btns {
      width: 100%;
      flex-direction: row !important;
      gap: 10px !important;
      margin-top: 10px;
    }

    .actions-btns .btn {
      flex: 1;
      font-size: 13px;
      padding: 8px 5px;
      white-space: nowrap;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    /* Offcanvas */
    .w-35 {
      width: 85% !important;
    }

    .commandes-panel h3 {
      font-size: 18px;
      margin-left: 10px !important;
      margin-right: 10px !important;
      margin-bottom: 20px !important;
    }

    .commande-inner.m-4 {
      margin: 15px !important;
    }

    .w-60 {
      width: 100% !important;
    }

    /* Table in offcanvas */
    .price-commande .table {
      font-size: 13px;
    }
    
    .price-commande .table th, 
    .price-commande .table td {
      padding: 8px 4px;
    }

    .w-30 {
      width: 20% !important;
    }

    .mt-4.d-flex.gap-3 {
      flex-direction: column;
      gap: 10px !important;
    }

    .mt-4.d-flex.gap-3 .btn {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    .w-35 {
      width: 100% !important;
    }

    .accordion-block-info-top {
      font-size: 13px;
    }

    .status-title {
      font-size: 11px;
      padding: 2px 6px;
    }

    .font-16 {
      font-size: 14px !important;
    }

    .font-15 {
      font-size: 13px !important;
    }

    .actions-btns {
      flex-direction: column !important;
    }
  }
</style>